window.Theme = window.Theme || {};


Theme.querySelectorAll = function (selector, context = document) {
  return Array.from(context.querySelectorAll(selector));
};


Theme.URLParams = {
  set: function (key, value) {
    const url = new URL(window.location.href);
    url.searchParams.set(key, value);
    history.replaceState(null, '', url);
  },

  get: function (key) {
    const url = new URL(window.location.href);
    return url.searchParams.get(key);
  },

  delete: function (key) {
    const url = new URL(window.location.href);
    url.searchParams.delete(key);
    history.replaceState(null, '', url);
  },

  has: function (key) {
    const url = new URL(window.location.href);
    return url.searchParams.has(key);
  }
};


Theme.Helpers = {
  preload_image: function (url) {
    if (!url) {
      return;
    }

    const img = new Image();
    img.src = url;
  },

  prefetch: function (url) {
    if (document.querySelector(`link[href="${url}"]`)) {
      return;
    }

    const link = document.createElement("link");

    link.rel = "prefetch";
    link.href = url;

    document.head.appendChild(link);
  },

  debounce: function (fn, delay) {
    let timeout;

    return function() {
      const context = this;
      const args = arguments;

      clearTimeout(timeout);

      timeout = setTimeout(() => {
        fn.apply(context, args);
      }, delay);
    };
  },

  throttle: function (callback, limit) {
    let lastCall = 0;

    return function () {
      const now = Date.now();

      if (now - lastCall >= limit) {
        lastCall = now;
        callback.apply(this, arguments);
      }
    };
  },

  scroll_to: function (id, offset = 80) {
    const target = document.getElementById(id);

    if (!target) {
      return;
    }

    const position = target.offsetTop - offset;

    window.scrollTo({
      top: position,
      behavior: "smooth"
    });
  }
};


Theme.Component = class extends HTMLElement {
  #elements = null;
  #data = null;
  #controller = new AbortController();
  #observers = new Set();

  constructor() {
    super();
  }

  connectedCallback() {
    this.addActionEvents();
  }

  get data() {
    if (this.#data != null) {
      return this.#data;
    }

    const element = this.querySelector(":scope > script[type='application/json']");

    if (!element) {
      return (this.#data = {});
    } else {
      return (this.#data = JSON.parse(element.textContent));
    }
  }

  get elements() {
    if (this.#elements) {
      return this.#elements;
    }

    this.#elements = {};
    this.getElements();
    this.getElementCollections();

    return this.#elements;
  }

  getElements() {
    this.querySelectorAll("[data-element]").forEach((element) => {
      const name = element.dataset.element;
      this.defineLazySelector(name, `[data-element="${name}"]`);
    });
  }

  getElementCollections() {
    this.querySelectorAll("[data-elements]").forEach((element) => {
      const name = element.dataset.elements;

      if (!this.#elements[name]) {
        this.defineLazySelector(name, `[data-elements="${name}"]`, true);
      }
    });
  }

  defineElements(map = {}) {
    this.#elements ||= {};

    for (const [name, selector] of Object.entries(map)) {
      this.defineLazySelector(name, selector);
    }
  }

  defineElementCollections(map = {}) {
    this.#elements ||= {};

    for (const [name, selector] of Object.entries(map)) {
      this.defineLazySelector(name, selector, true);
    }
  }

  defineLazySelector(name, selector, collection = false) {
    Object.defineProperty(this.#elements, name, {
      configurable: true,
      get: () => {
        let element = null;

        if (collection) {
          element = Array.from(this.querySelectorAll(selector));
        } else {
          element = this.querySelector(selector);
        }

        Object.defineProperty(this.#elements, name, { value: element });

        return element;
      }
    });
  }

  addActionEvents() {
    const elements = this.querySelectorAll("[data-action]");

    elements.forEach((element) => {
      const [event, method] = element.dataset.action.split(":");
      const detail = JSON.parse(element.dataset.detail || "{}");

      this.addEvent(event, element, () => {
        this[method](detail, element);
      });
    });
  }

  addEvent(type, target, listener) {
    this.addEventListener(type, (event) => {
      if (this.closestElement(event.target, target)) {
        listener.call(this, event);
        event.stopPropagation();
      }
    }, { signal: this.#controller.signal });
  }

  addDirectEvent(type, target, listener) {
    target.addEventListener(type, (event) => {
      listener.call(this, event);
    }, { signal: this.#controller.signal });
  }

  closestElement(starting, matching) {
    let current = starting;

    while (current) {
      if (current === matching) {
        return current;
      }

      current = current.parentElement;
    }

    return null;
  }

  intersectionObserver(fn, options = { threshold: 0.25 }) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        fn(entry, observer);
      });
    }, options);

    this.#observers.add(observer);
    observer.observe(this);

    return observer;
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#controller = new AbortController();

    this.#observers.forEach(observer => observer.disconnect());
    this.#observers.clear();
  }
}
